<template>
  <footer class="footer">
    <div class="footer-container position-relative">
      <transition name="fade">
        <button
          v-if="showScrollTop"
          @click="scrollToTop"
          class="scroll-top-btn"
          aria-label="Scroll to top"
        >
          <i class="fas fa-chevron-up"></i>
        </button>
      </transition>

      <div class="footer-shell">
        <div class="footer-brand">
          <div class="brand-row">
            <span class="brand-mark">
              <img src="/favicon.png" alt="Invi8" class="brand-logo" />
            </span>
            <div>
              <p class="eyebrow">Event workflows made simple</p>
              <h5 class="brand-title mb-1">Invi8</h5>
              <p class="tagline mb-0">Plan, share, and celebrate with a platform built for memorable moments.</p>
            </div>
          </div>
        </div>

        <div class="footer-links">
          <h6 class="section-label">Support</h6>
          <ul class="list-unstyled mb-0">
            <li>
              <a href="https://invi8.net/support" target="_blank" rel="noopener">
                <i class="fas fa-life-ring"></i>
                Help & Support
              </a>
            </li>
            <li>
              <a href="https://invi8.net/privacy-policy" target="_blank" rel="noopener">
                <i class="fas fa-shield-alt"></i>
                Privacy Policy
              </a>
            </li>
            <li>
              <a href="https://invi8.net/terms-conditions" target="_blank" rel="noopener">
                <i class="fas fa-file-signature"></i>
                Terms & Conditions
              </a>
            </li>
          </ul>
        </div>

        <div class="footer-cta">
          <h6 class="section-label">Stay connected</h6>
          <p class="cta-copy mb-3">
            New features, smoother invites, and thoughtful updates. We build with hosts and guests in mind.
          </p>
          <div class="pill-row">
            <span class="pill">
              <i class="fas fa-lock"></i> Secure by default
            </span>
            <span class="pill">
              <i class="fas fa-bolt"></i> Fast sharing
            </span>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <p class="mb-0">
          &copy; {{ currentYear }}
          <span class="credits">Invi8</span>
        </p>
        <div class="meta-links">
          <span>Crafted for modern celebrations</span>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

// Reactive current year
const currentYear = ref(new Date().getFullYear())

// Show/hide scroll-to-top button
const showScrollTop = ref(false)

function handleScroll() {
  // Show button when user scrolled down more than 300px
  showScrollTop.value = window.scrollY > 300
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.footer {
  --brand: #0f172a;
  --brand-strong: #0b1223;
  --muted: #cbd5e1;
  --accent: #0f172a;
  background: radial-gradient(circle at 12% 20%, rgba(255, 255, 255, 0.08), transparent 25%),
    radial-gradient(circle at 88% 10%, rgba(255, 255, 255, 0.07), transparent 22%),
    linear-gradient(135deg, #0f172a 0%, #0b1223 65%, #09101f 100%);
  color: #e2e8f0;
  font-size: 14px;
  position: relative;
  padding: 56px 0 38px;
  overflow: hidden;
}

.footer::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, transparent 40%);
  pointer-events: none;
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.footer-shell {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.footer-brand .brand-row {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.brand-mark {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: #e2e8f0;
  color: var(--brand);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
}

.brand-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.eyebrow {
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #cbd5e1;
  font-size: 11px;
  margin-bottom: 6px;
}

.brand-title {
  color: #fff;
  font-size: 20px;
  letter-spacing: 0.02em;
}

.tagline {
  color: #cbd5e1;
  max-width: 420px;
}

.section-label {
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 12px;
  color: #cbd5e1;
  margin-bottom: 10px;
}

.footer-links ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 8px;
}

.footer-links a {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #f8fafc;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  transition: color 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

.footer-links a:hover {
  color: #f8fafc;
  border-color: rgba(255, 255, 255, 0.15);
  transform: translateX(2px);
}

.footer-links i {
  color: #e2e8f0;
  width: 18px;
  text-align: center;
}

.footer-cta {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
}

.cta-copy {
  color: #cbd5e1;
}

.pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  color: #f8fafc;
  font-weight: 600;
  font-size: 13px;
}

.scroll-top-btn {
  position: fixed;
  bottom: 25px;
  right: 25px;
  background: var(--brand);
  color: #f8fafc;
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  font-size: 20px;
  line-height: 48px;
  text-align: center;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
  z-index: 1050;
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.scroll-top-btn:hover {
  transform: scale(1.1);
  background: #132038;
}

/* Fade transition for button */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.footer-bottom {
  position: relative;
  z-index: 1;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #cbd5e1;
}

.credits {
  font-weight: 700;
  color: #fff;
}

.meta-links {
  display: flex;
  gap: 12px;
  align-items: center;
  color: #cbd5e1;
  font-size: 13px;
}

@media (max-width: 768px) {
  .footer-container {
    padding: 0 18px;
  }

  .scroll-top-btn {
    bottom: 20px;
    right: 20px;
    width: 44px;
    height: 44px;
    font-size: 18px;
  }

  .footer-bottom {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
}
</style>
